import { useEffect, useState } from "react";
import { FileText, Download, RefreshCw, Loader2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KEY = "contractor_agreement";

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function AdminDocuments() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/documents").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const info = data?.available?.[KEY];
  const doc = data?.documents?.find((x) => x.key === KEY);

  const generate = async () => {
    setBusy("generate");
    try {
      await api.post(`/admin/documents/${KEY}/generate`, {}, { timeout: 120000 });
      toast.success(doc ? "Agreement regenerated" : "Agreement ready to download");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate the agreement");
    } finally { setBusy(""); }
  };

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.get(`/admin/documents/${KEY}/download`, { params: { fmt }, responseType: "blob", timeout: 60000 });
      saveBlob(r.data, `${doc.filename}.${fmt}`);
    } catch {
      toast.error("Download failed — try Regenerate");
    } finally { setBusy(""); }
  };

  if (data === null) return null;

  return (
    <div className="dg-card rounded-xl p-5" data-testid="admin-documents-card">
      <div className="flex items-center gap-2">
        <ScrollText className="w-4 h-4 text-[#FFD600]" />
        <div className="dg-eyebrow">Documents</div>
        {doc && <span className="dg-chip ml-auto text-emerald-300" data-testid="doc-version-chip">v{doc.version} · {new Date(doc.generated_at).toLocaleDateString()}</span>}
      </div>
      <h3 className="font-display text-lg font-bold mt-3" data-testid="doc-title">{info?.title || "Partner Technician Agreement"}</h3>
      <p className="text-sm text-slate-400 mt-1">{info?.summary}</p>
      <p className="text-xs text-amber-200/90 mt-3" data-testid="doc-disclaimer">
        Template, not legal advice — fill in the blanks (business terms), then have a California attorney review before anyone signs.
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        {doc && (
          <>
            <button type="button" onClick={() => download("pdf")} disabled={!!busy} className="dg-btn-primary rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="doc-download-pdf">
              {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
            </button>
            <button type="button" onClick={() => download("docx")} disabled={!!busy} className="dg-btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="doc-download-docx">
              {busy === "docx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Download Word (.docx)
            </button>
          </>
        )}
        <button type="button" onClick={generate} disabled={!!busy} className={`${doc ? "dg-btn-ghost" : "dg-btn-primary"} rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50`} data-testid="doc-generate-btn">
          {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {doc ? "Regenerate" : "Generate agreement"}
        </button>
      </div>
    </div>
  );
}
